import {Box, Typography} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, {ReactNode, useEffect, useRef, useState} from "react";
import { connect } from "react-redux";
import { selectModeData } from "../../redux/selectors/editor";
import { selectTags } from "../../redux/selectors/firebase";
import { ActionFactory } from "../../fstn/actionFactory";
import { Cursor } from "../../fstn/cursor";
import { Network } from "../../fstn/network";
import { ITag, Tags } from "../graph/StateTags";
import { parseScriptParts } from "../scriptParser";
import { Goto } from "./Goto";
import Line from "./Line";
import Page from "./Page";
import PageCount from "./PageCount";
import { IScriptData, UpdateModeData, updateModeData } from "../../redux/slices/editor";
import { exportDocX } from "./exportDocX";
import Paragraph from "./Paragraph";
import {getBlockID} from "../../utility/blockNames";

const useStyles = makeStyles((theme) => ({
    blockName: {
        fontFamily: "inherit",
        fontSize: "inherit",
        fontStyle: "italic"
    },
    blockNames: {
        marginRight: "5mm",
        marginBottom: "3mm",
        textAlign: "right"
    },
    content: {
        overflowY: "auto",
        height: "100%",
        fontFamily: "Courier New",
        "@media print": {
            overflowY: "visible",
            height: "fit-content"
        }
    },
    pageCount: {
        position: "fixed",
        left: theme.spacing(1),
        bottom: theme.spacing(1)
    }
}));

interface ILine {
    state: string,
    line: string
}
interface IGoto {
    state: string,
    trigger?: string
}
interface IDestinations {
    goto: IGoto[]
}
interface IParagraph {
    blocks: {[state: string]: string[]},
    lines: ILine[],
    page: number,
    destinations?: IDestinations,
    scene: string | null
}
interface IPage {
    content: IParagraph[],
    height: number
}
interface Props
{
    network: Network
    factory: ActionFactory
    tags: Tags
    scriptData: IScriptData
    updateModeData: UpdateModeData
}
function PagedScript({ tags, network, factory, scriptData, updateModeData }: Props)
{
    const [script, setScript] = useState<ReactNode[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageTotal, setPageTotal] = useState<number>(0);
    const [heights, setHeights] = useState<{ [id: string]: number }>({});
    const ref = useRef<HTMLDivElement>(null);
    const classes = useStyles();

    const handleScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) =>
    {
        const scrollPage = Math.round((event.currentTarget.scrollTop / event.currentTarget.scrollHeight) * pageTotal) + 1;
        if (scrollPage !== currentPage)
            setCurrentPage(scrollPage);
    }
    const handleSetHeight = (id: string, height: number) =>
    {
        setHeights((heights) =>
        {
            if (heights[id] !== height)
            {
                heights[id] = height;
                return { ...heights };
            }

            return heights;
        });
    }

    useEffect(() =>
    {
        if (!network || Object.keys(tags).length === 0)
            return;

        const maxSectionHeight = 1080;
        const lineHeight = 25;

        const cursor = new Cursor(network, factory);

        const destinationCount: { [state: string]: number } = {};

        network.states.forEach((stateData, state) =>
        {
            stateData.transitions.forEach((transition) =>
            {
                const stateData = network.states.get(state);
                if (transition.destination && transition.trigger && stateData?.meta.type !== "jump")
                {
                    cursor.forceState(state);
                    cursor.trigger(transition.trigger);
                    const destinationState = cursor.state;
                    const destinationData = network.states.get(destinationState);
                    const destinationParent = network.states.get(destinationData?.parent ?? "");
                    if (stateData?.parent !== destinationParent?.parent && destinationData?.meta.type !== "jump")
                    {
                        destinationCount[destinationState] = destinationCount[destinationState] ?? 0;
                        destinationCount[destinationState]++;
                    }
                }
            });
        });

        Object.keys(destinationCount).forEach((destination) =>
        {
            if (destinationCount[destination] <= 1)
            {
                delete destinationCount[destination];
            }
        });

        const pages: IPage[] = [];
        let pageNo = 0;

        const newPage = () =>
        {
            const p = pageNo++;
            return pages[p] = { content: [], height: 0 };
        }

        const newParagraph = (scene: string | null = null) =>
        {
            const paragraph: IParagraph = { blocks: {}, lines: [], page: pageNo, scene };
            page.content.push(paragraph);
            page.height += lineHeight; // paragraph heading height
            if (scene)
            {
                page.height += lineHeight;
            }
            return paragraph;
        }

        const newBranches = (): IDestinations => 
        {
            return { goto: [] };
        }



        let page: IPage = newPage();
        let paragraph: IParagraph | null = null;

        const clearParagraph = (state: string) =>
        {
            if (paragraph && paragraph.lines.length > 0)
            {
                paragraph.destinations = newBranches();
                paragraph.destinations.goto = [{ state }];
                paragraph = null;
            }
        }

        const checkPageHeight = (state: string, height: number) =>
        {
            if (page.height + height > maxSectionHeight)
            {
                if (paragraph && paragraph.lines.length > 0)
                {
                    clearParagraph(state);
                }
                page = newPage();
                return true;
            }

            return false;
        }

        const visited: string[] = [];

        let lastScene = "";

        const continueState: string[] = [];

        const addStateToScript = (cursor: Cursor) =>
        {
            let parent: string | undefined;
            while (!visited.includes(cursor.state))
            {
                const state = cursor.state;
                const stateData = network.states.get(state);
                const stateParent = stateData?.parent;
                if (parent && stateParent !== parent)
                {
                    continueState.push(state);
                    return;
                }
                parent = stateParent;
                if (destinationCount[state] && destinationCount[state] > 0)
                {
                    destinationCount[state]--;
                    page.height += lineHeight;
                    if (paragraph)
                    {
                        paragraph.destinations = newBranches();
                        paragraph.destinations.goto = [{ state }];
                    }
                    if (destinationCount[state] > 0)
                    {
                        // need to complete other branches first so break here
                        break;
                    }
                    // branch has converged so start a new paragraph
                    paragraph = null;
                }

                visited.push(state);

                if (stateData)
                {
                    const scriptParts = parseScriptParts(stateData);
                    const gotoHeights = stateData.transitions.length * lineHeight;
                    const line = scriptParts.text || "";
                    const stateHeight = heights[state] ?? 0;
                    checkPageHeight(state, stateHeight + gotoHeights)

                    let displayScene: string | null = null;
                    if (line.length > 0)
                    {
                        const sceneTags: ITag[] = Object.values(tags[state] ?? {}).filter(tag => tag.type === "scene");
                        if (sceneTags && sceneTags.length > 0)
                        {
                            const scene = sceneTags[0].value.toString();

                            if (!paragraph?.lines.every((line) => line.line.length === 0)) // Fixes issues with scene chances between nodes with no script
                            {
                                if (scene !== lastScene)
                                {
                                    displayScene = scene;
                                    lastScene = scene;
                                    clearParagraph(state);
                                }
                            }
                            else
                            {
                                paragraph.scene = scene;
                            }
                        }
                    }

                    paragraph = paragraph ?? newParagraph(displayScene);
                    paragraph.blocks[state] = (stateData.meta.blockNames || []).map(getBlockID);
                    paragraph.lines.push({ state, line });
                    page.height += stateHeight;

                    if (stateData.transitions.length > 1)
                    {
                        paragraph.destinations = newBranches();
                        paragraph.destinations.goto = stateData.transitions.map((transition) =>
                        {
                            cursor.forceState(state);
                            cursor.trigger(transition.trigger ?? "");
                            let destination = cursor.state;
                            if (destination)
                            {
                                const stateDetails = network.states.get(destination);
                                if (stateDetails && stateDetails.meta.type === "jump" && stateDetails.transitions.length === 1)
                                {
                                    cursor.trigger("continue");
                                    destination = cursor.state;
                                }
                            }

                            return {
                                state: destination ?? "",
                                trigger: transition.trigger ?? ""
                            }
                        });

                        page.height += stateData.transitions.length * lineHeight;

                        for (const goto of paragraph.destinations.goto)
                        {
                            paragraph = null;
                            cursor.forceState(goto.state);
                            addStateToScript(cursor);
                        }

                        break;
                    }
                    else
                    {
                        cursor.trigger("continue");
                    }
                }
            }

            if (paragraph && !paragraph.destinations && !paragraph.lines.find((line) => line.state === cursor.state))
            {
                paragraph.destinations = newBranches();
                paragraph.destinations.goto = [{ state: cursor.state }];
            }
        }

        cursor.reset();
        continueState.push(cursor.state);
        while (continueState.length !== 0)
        {
            cursor.forceState(continueState.shift() as string);
            addStateToScript(cursor);
        }

        const statePageMap: { [state: string]: number } = {};

        const getState = (page: IPage) =>
        {
            for (const content of page.content)
            {
                if (content.lines.length > 0)
                {
                    return content.lines[0].state;
                }
                else if (content.destinations && content.destinations.goto.length > 0)
                {
                    return content.destinations.goto[0].state;
                }
            }

            return "";
        }

        const stateParagraphMap: { [state: string]: number } = {};
        let paragraphCount = 0;

        // Populate stateParagraphMap
        pages.forEach(page =>
            page.content.forEach(paragraph => {
                ++paragraphCount;
                paragraph.lines.forEach(line =>
                    stateParagraphMap[line.state] = paragraphCount
                );
            })
        );
        const script = pages.map((page, pageIndex) =>
        {
            const pageNo = pageIndex + 1;
            const pageParagraphs = { min: -1, max: -1 };

            return <Page
                key={pageIndex} page={pageNo} state={getState(page)} paragraphs={pageParagraphs}>
                {page.content.map((paragraph) =>
                {
                    const elements: JSX.Element[] = [];
                    const lines = paragraph.lines;

                    if (lines.length > 0)
                    {
                        const paragraphNo = stateParagraphMap[lines[0].state];

                        elements.push(
                            <Paragraph key={paragraphNo} paragraphNo={paragraphNo}>
                                {
                                    lines.map((lineData, index) =>
                                    {
                                        pageParagraphs.min = pageParagraphs.min === -1 ? paragraphNo : pageParagraphs.min;
                                        pageParagraphs.max = paragraphNo;
                                        statePageMap[lineData.state] = pageIndex + 1;
                                        return (
                                            <Box key={index}>
                                                <Box className={classes.blockNames}>
                                                    {paragraph.blocks[lineData.state].map((blockID, index) =>
                                                        <Typography className={classes.blockName} key={index}>
                                                            {blockID}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {lineData.line.length > 0 &&
                                                    <Line key={lineData.state} id={lineData.state}
                                                          script={lineData.line} setHeight={handleSetHeight}
                                                          tags={Object.values(tags[lineData.state] || {})}/>
                                                }
                                            </Box>
                                        );
                                    })
                                }
                            </Paragraph>);
                        if (paragraph.destinations)
                        {
                            const goto = paragraph.destinations.goto;

                            if (goto.length > 1 || paragraphNo + 1 !== stateParagraphMap[goto[0].state]) {
                                elements.push(...goto.map((goto, gotoIndex) =>
                                    <Goto key={`${pageIndex + 1}-${goto.state}.${gotoIndex}`} trigger={goto.trigger}
                                          state={goto.state} pages={statePageMap} paragraphs={stateParagraphMap}/>
                                ));
                            }
                        }
                    }
                    return elements;
                })}
            </Page >
        });

        setScript(script);
        setPageTotal(script.length);
    }, [classes, network, factory, heights, tags]);

    useEffect(() =>
    {
        if (scriptData.exportDocX && ref.current)
        {
            exportDocX(ref.current).then(() => updateModeData({ exportDocX: false }));
        }
    }, [classes, scriptData, updateModeData]);
    return (
        <Box>
            <div ref={ref} onScroll={handleScroll} className={classes.content}>
                {script}
            </div>
            <PageCount page={currentPage} total={pageTotal} />
        </Box>
    );
}

const mapStateToProps = (store: never) => ({
    tags: selectTags(store) || {},
    scriptData: selectModeData(store) as IScriptData,
});
export default connect(mapStateToProps, {
    updateModeData
})(PagedScript);